import React, { useState } from 'react';
import { graphqlRequest } from '../../service/graphql-client';
import { GQ_SEND_RESET_CODE, GQ_VERIFY_RESET_CODE, GQ_COMPLETE_PASSWORD_RESET } from '../../service/graphql-mutations';

export default function ForgotPasswordModal({ onClose: _onClose, onShowLogin }) {
  const [step, setStep] = useState(1); // 1: email, 2: verify code, 3: reset password
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'email') {
      setEmail(value);
    } else if (name === 'verificationCode') {
      setVerificationCode(value);
    } else if (name === 'newPassword') {
      setNewPassword(value);
    } else if (name === 'confirmPassword') {
      setConfirmPassword(value);
    }

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSendResetCode = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const data = await graphqlRequest(GQ_SEND_RESET_CODE, { email });

      if (data.sendResetCode && data.sendResetCode.success) {
        setSuccessMessage(data.sendResetCode.message || 'Password reset code sent to your email');
        setStep(2);
      } else {
        setErrorMessage(data.sendResetCode?.message || 'Failed to send password reset code. Please try again.');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to send password reset code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyResetCode = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const data = await graphqlRequest(GQ_VERIFY_RESET_CODE, {
        email: email,
        code: verificationCode
      });

      if (data.verifyResetCode && data.verifyResetCode.success) {
        setSuccessMessage(data.verifyResetCode.message || 'Verification code is valid');
        setStep(3);
      } else {
        setErrorMessage(data.verifyResetCode?.message || 'Invalid or expired verification code');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompletePasswordReset = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage('Password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }

    try {
      const data = await graphqlRequest(GQ_COMPLETE_PASSWORD_RESET, {
        email: email,
        newPassword: newPassword
      });

      if (data.completePasswordReset && data.completePasswordReset.success) {
        setSuccessMessage(data.completePasswordReset.message || 'Password has been reset successfully');
        // Reset form and go back to login after a delay
        setTimeout(() => {
          onShowLogin();
        }, 2000);
      } else {
        setErrorMessage(data.completePasswordReset?.message || 'Failed to reset password');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <>
      <div className="modal-header">
        <h2 className="modal-title">Forgot Password</h2>
      </div>
      <div className="modal-body saveBuildModalBody">
        <p>Enter your email address and we'll send you a password reset code.</p>
        <form onSubmit={handleSendResetCode}>
          <div>
            <label htmlFor="email">Email Address:</label> <br />
            <input
              className="input_100"
              name="email"
              type="email"
              value={email}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <button
              type="submit"
              className="orange-button button saveBuildButton"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </div>
        </form>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        <div className="backToLogin">
          <a href="javascript:void(0)" onClick={onShowLogin} className="link-button">Back to Login</a>
        </div>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="modal-header">
        <h2 className="modal-title">Verify Reset Code</h2>
      </div>
      <div className="modal-body saveBuildModalBody">
        <p>We've sent a verification code to {email}. Please enter it below.</p>
        <form onSubmit={handleVerifyResetCode}>
          <div>
            <label htmlFor="verificationCode">Verification Code:</label> <br />
            <input
              className="input_100"
              name="verificationCode"
              type="text"
              value={verificationCode}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <button
              type="submit"
              className="orange-button button saveBuildButton"
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify Code'}
            </button>
          </div>
        </form>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        <div className="backToLogin">
          <a href="javascript:void(0)" onClick={() => setStep(1)} className="link-button">Back to Email</a>
        </div>
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <div className="modal-header">
        <h2 className="modal-title">Reset Password</h2>
      </div>
      <div className="modal-body saveBuildModalBody">
        <p>Enter your new password below.</p>
        <form onSubmit={handleCompletePasswordReset}>
          <div>
            <label htmlFor="newPassword">New Password:</label> <br />
            <input
              className="input_100"
              name="newPassword"
              type="password"
              value={newPassword}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <label htmlFor="confirmPassword">Confirm New Password:</label> <br />
            <input
              className="input_100"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <button
              type="submit"
              className="orange-button button saveBuildButton"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </div>
        </form>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        <div className="backToLogin">
          <a href="javascript:void(0)" onClick={() => setStep(2)} className="link-button">Back to Verification</a>
        </div>
      </div>
    </>
  );

  switch (step) {
    case 1:
      return renderStep1();
    case 2:
      return renderStep2();
    case 3:
      return renderStep3();
    default:
      return renderStep1();
  }
}